import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import WorkCard from '@/components/base/WorkCard';
import NewsCard from '@/components/base/NewsCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { news } from '@/mocks/news';
import { works } from '@/mocks/works';

type FilterKey = 'all' | 'movie' | 'tv' | 'news';

const filters: { key: FilterKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'news', label: '资讯' },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const [keyword, setKeyword] = useState(query);
  const [filter, setFilter] = useState<FilterKey>('all');

  usePageMeta(query ? `“${query}” 搜索结果 - 枫与铃` : '综合搜索 - 枫与铃', '在枫与铃搜索电影、电视剧与影视资讯。');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setParams(q ? { q } : {});
  };

  const kw = query.trim().toLowerCase();

  const matchedWorks = useMemo(() => {
    if (!kw) return [];
    return works.filter((w) => {
      const haystack = [w.title, w.director, ...w.genres, ...w.cast, w.summary, w.region].join(' ').toLowerCase();
      return haystack.includes(kw);
    });
  }, [kw]);

  const matchedNews = useMemo(() => {
    if (!kw) return [];
    return news.filter((n) => [n.title, n.summary, n.category].join(' ').toLowerCase().includes(kw));
  }, [kw]);

  const showWorks = filter === 'all' || filter === 'movie' || filter === 'tv';
  const showNews = filter === 'all' || filter === 'news';

  const visibleWorks = matchedWorks.filter((w) => (filter === 'all' ? true : w.type === filter));
  const visibleNews = showNews ? matchedNews : [];

  const total = (showWorks ? visibleWorks.length : 0) + visibleNews.length;

  const hasQuery = kw.length > 0;
  const hotWorks = [...works].sort((a, b) => a.rank - b.rank).slice(0, 4);
  const hotNewsList = news.slice(0, 3);

  return (
    <div className="w-full">
      <div className="w-full bg-primary-900 py-10 md:py-14">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          <h1 className="font-heading text-2xl font-black text-background-50 md:text-3xl">综合搜索</h1>
          <p className="mt-2 text-sm text-primary-100">输入关键词，快速查找你感兴趣的电影、电视剧与影视资讯。</p>
          <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-primary-300" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="例如：悬疑、星际、古装…"
                className="w-full rounded-md border border-primary-700 bg-primary-800/70 py-3 pl-11 pr-4 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/40"
              />
            </div>
            <button
              type="submit"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        {hasQuery && (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-700">
              {total > 0 ? (
                <>
                  找到 <span className="font-semibold text-primary-600">{total}</span> 条与「
                  <span className="font-semibold text-foreground-950">{query}</span>」相关的结果
                </>
              ) : (
                <>未找到与「{query}」相关的内容</>
              )}
            </p>
          </div>
        )}

        {hasQuery && (
          <div className="mt-4 inline-flex flex-wrap items-center gap-1 rounded-full bg-secondary-100 p-1">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  filter === f.key ? 'bg-primary-500 text-background-50' : 'text-secondary-900 hover:bg-secondary-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {!hasQuery && (
          <p className="rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-600">
            输入关键词开始搜索，或先看看下方的热门推荐。
          </p>
        )}

        {hasQuery && total > 0 && (
          <div className="mt-6 space-y-10">
            {showWorks && visibleWorks.length > 0 && (
              <section>
                <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">影视作品</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
                  {visibleWorks.map((w) => (
                    <WorkCard key={w.id} work={w} />
                  ))}
                </div>
              </section>
            )}
            {visibleNews.length > 0 && (
              <section>
                <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">影视资讯</h2>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {visibleNews.map((n) => (
                    <NewsCard key={n.id} item={n} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {hasQuery && total === 0 && (
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-400" />
            <p className="mt-4 text-lg font-medium text-foreground-800">未找到相关内容</p>
            <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或看看下面为你准备的热门推荐。</p>
          </div>
        )}

        {(!hasQuery || (hasQuery && total === 0)) && (
          <div className="mt-10 space-y-10">
            <section>
              <div className="mb-4 flex items-center gap-2">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="font-heading text-lg font-bold text-foreground-950">热门影视推荐</h2>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
                {hotWorks.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </section>
            <section>
              <div className="mb-4 flex items-center gap-2">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="font-heading text-lg font-bold text-foreground-950">热门资讯</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {hotNewsList.map((n) => (
                  <NewsCard key={n.id} item={n} />
                ))}
              </div>
            </section>
            <div className="text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line" />
                返回首页
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}